// src/workflow/MyWorkflowList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge, Box, Button, HStack, Link, Spinner, Stack,
  Table, Tbody, Td, Th, Thead, Tr, Text, IconButton, Tooltip
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
const statusColor = (s) =>
  ({ new:"gray", creating:"purple", running:"green", in_progress:"blue", paused:"orange", completed:"green", failed:"red" }[String(s||"").toLowerCase()] || "gray");

async function apiGet(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export default function MyWorkflowList({ baseUrl = "https://workflow.poc.mrc.ai", typeFilter }) {
  const [loading, setLoading] = useState(true);
  const [headers, setHeaders] = useState([]);
  const navigate = useNavigate();

  const headersUrl = useMemo(() => {
    const u = new URL("/api/temporal/activities", baseUrl);
    if (typeFilter) u.searchParams.set("type", typeFilter);
    return u.toString();
  }, [baseUrl, typeFilter]);

  const loadHeaders = async () => {
    setLoading(true);
    try {
      const data = await apiGet(headersUrl);
      console.log("Workflow payload:", data);
      setHeaders(data);
    } catch {
      setHeaders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHeaders(); /* eslint-disable-next-line */ }, [headersUrl]);

  return (
    <Stack spacing={4}>
      <HStack justify="space-between">
        <Tooltip label="Refresh list">
          <IconButton aria-label="Refresh" icon={<RepeatIcon />} onClick={loadHeaders} />
        </Tooltip>
      </HStack>

      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <HStack justify="space-between">
          {loading && (
            <HStack color="gray.500" fontSize="sm">
              <Spinner size="sm" /><Text>Loading…</Text>
            </HStack>
          )}
        </HStack>

        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Created</Th>
                <Th>Status</Th>
                <Th>Open</Th>
                <Th>Payload</Th>
              </Tr>
            </Thead>
            <Tbody>
              {headers
                .filter((h) => String(h.status || "").toLowerCase() !== "completed")
                .map((h) => (
                <Tr key={h._id}>
                  <Td>
                    <Stack spacing={0}>
                      <Link as={RouterLink} to={`/workflow/${h._id}`} fontSize="xs">
                        {h.name || "—"}
                      </Link>
                    </Stack>
                  </Td>
                  <Td fontSize="xs">{h.type || "—"}</Td>
                  <Td fontSize="xs">{fmtDate(h.createdDate)}</Td>
                  <Td><Badge colorScheme={statusColor(h.status)}>{h.status || "—"}</Badge></Td>
                  <Td>
                    <Button size="xs" variant="outline" onClick={() => navigate(`/workflow/${h._id}`)}>
                      View
                    </Button>
                  </Td>
                  <Td>
                    <Button
                      size="xs"
                      onClick={() =>
                        navigate(`/workflow/${h._id}/payload`, {
                          state: { jsonString: JSON.stringify(h.payload ?? {}, null, 2) }
                        })
                      }
                    >
                      View Payload
                    </Button>
                  </Td>
                </Tr>
              ))}
              {!loading && headers.length === 0 && (
                <Tr><Td colSpan={6} color="gray.500">No workflows found.</Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </Stack>
  );
}
